import axios from 'axios';
import { SignInResponse, UserDetailsResponse } from '../modules/auth/types';
import { LOCAL_KEY, LOCAL_USERDATA_KEY } from '../variables';

const CLIENT_CREDENTIAL_URL = `${import.meta.env.VITE_ARIDE_API_URL}/v2/auth/client-credential`;

const MAX_REFRESH_ATTEMPTS = 3;

export class TokenRefreshError extends Error {
  constructor(
    message: string = 'Não foi possível renovar o token. Tente fazer login novamente.',
    public readonly attempts: number
  ) {
    super(message);
    this.name = 'TokenRefreshError';
  }
}

interface AuthDataResponse {
  authData: SignInResponse | undefined;
  userData: UserDetailsResponse | undefined;
}
class TokenService {
  setAuthData(authData: SignInResponse, userData: UserDetailsResponse) {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(authData));
    localStorage.setItem(LOCAL_USERDATA_KEY, JSON.stringify(userData));
  }

  getAuthData(): AuthDataResponse {
    const authDataString = localStorage.getItem(LOCAL_KEY);
    const authData = authDataString && JSON.parse(authDataString);
    const userDataString = localStorage.getItem(LOCAL_USERDATA_KEY);
    const userData = userDataString && JSON.parse(userDataString);

    return { authData, userData };
  }
  getClientCredencialtoken(): string | null {
    const clientCredential = localStorage.getItem(LOCAL_KEY);
    return clientCredential;
  }

  async getClientCredencial(): Promise<string> {
    try {
      const response = await axios.post<{ data: { access_token: string } }>(CLIENT_CREDENTIAL_URL, {
        realm: import.meta.env.VITE_AUTH_REALM,
        clientId: import.meta.env.VITE_AUTH_CLIENT_ID
      });
      const accessToken = response.data?.data?.access_token;
      if (accessToken) {
        localStorage.setItem(LOCAL_KEY, accessToken);
        return accessToken;
      }
    } catch (error: unknown) {
      console.error((error as { message?: string })?.message);
      throw error;
    }
    throw new Error('Falha ao obter token de client credential');
  }

  /**
   * Tenta renovar o token (client credential) até MAX_REFRESH_ATTEMPTS vezes.
   * Usado quando o token expira no meio do fluxo (ex.: checkout).
   * @throws {TokenRefreshError} Após 3 tentativas sem sucesso
   */
  async refreshClientCredentialWithRetry(): Promise<string> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt++) {
      try {
        const token = await this.getClientCredencial();
        return token;
      } catch (err) {
        lastError = err;
        if (attempt === MAX_REFRESH_ATTEMPTS) {
          throw new TokenRefreshError(
            'Não foi possível renovar o token após 3 tentativas. Tente fazer login novamente.',
            MAX_REFRESH_ATTEMPTS
          );
        }
      }
    }
    throw lastError;
  }

  removeAuthData(): void {
    localStorage.removeItem(LOCAL_KEY);
    localStorage.removeItem(LOCAL_USERDATA_KEY);
  }
}

export default new TokenService();
